// NotificationCenter.jsx
import React, { useMemo, useState } from 'react';
import { Check, Trash2, Bell, MoreVertical } from 'lucide-react';
import TokenChecker from './TokenChecker';
import { error as logError } from "../utils/logger";

// tiny classnames helper
const cn = (...args) => args.filter(Boolean).join(' ');

// ---- inline scrollbar styles (works without plugins) ----
const ScrollbarStyles = () => (
  <style>{`
    .thin-scroll {
      scrollbar-width: thin;           /* Firefox */
      scrollbar-color: #A0AEC0 transparent;
    }
    .thin-scroll::-webkit-scrollbar {  /* WebKit */
      width: 8px;
      height: 8px;
    }
    .thin-scroll::-webkit-scrollbar-track {
      background: transparent;
    }
    .thin-scroll::-webkit-scrollbar-thumb {
      background: #CBD5E0;
      border-radius: 9999px;
    }
    .thin-scroll::-webkit-scrollbar-thumb:hover {
      background: #A0AEC0;
    }
  `}</style>
);

// ---- time + grouping helpers ----
function timeAgo(dateInput) {
  if (!dateInput) return '';
  const d = new Date(dateInput);
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  const days = Math.floor(diff / 86400);
  return `${days} day${days > 1 ? 's' : ''} ago`;
}

function sectionLabel(date) {
  if (!date) return 'Earlier';
  const now = new Date();
  const d = new Date(date);
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();

  if (sameDay) return 'Today';

  const y = new Date(now);
  y.setDate(now.getDate() - 1);
  const yesterday =
    d.getFullYear() === y.getFullYear() &&
    d.getMonth() === y.getMonth() &&
    d.getDate() === y.getDate();
  if (yesterday) return 'Yesterday';

  // start of week (Mon)
  const start = new Date(now);
  const dow = (now.getDay() + 6) % 7; // 0=Mon
  start.setDate(now.getDate() - dow);
  start.setHours(0, 0, 0, 0);

  if (d >= start) return 'This Week';
  return 'Earlier';
}

function groupByDate(items) {
  const sections = {};
  for (const n of items) {
    const label = sectionLabel(n.created_at);
    if (!sections[label]) sections[label] = [];
    sections[label].push(n);
  }
  return sections;
}

// color dot for type
function TypePill({ type = 'info', isRead }) {
  const map = {
    success: 'bg-emerald-500',
    warning: 'bg-amber-500',
    error: 'bg-red-500',
    info: 'bg-slate-500',
  };
  const color = map[type] || map.info;
  return (
    <span
      className={cn(
        'inline-block h-2.5 w-2.5 rounded-full ring-2 ring-white',
        isRead ? 'opacity-50' : 'opacity-100',
        color
      )}
    />
  );
}

function Empty() {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <Bell className="h-10 w-10 text-slate-300 mb-3" aria-hidden />
      <p className="text-slate-700 text-sm">You’re all caught up.</p>
      <p className="text-slate-500 text-xs">New alerts will appear here.</p>
    </div>
  );
}

function Item({ n, onRead, onDelete }) {
  const timeLabel = n.created_at ? timeAgo(n.created_at) : '';

  return (
    <div
      className={cn(
        // ↓ slightly reduced vertical padding (py-2 vs py-2.5) and tighter gap
        'group relative flex gap-2.5 rounded-lg border bg-white px-3 py-2 shadow-sm transition-colors',
        !n.is_read && 'border-slate-300 bg-slate-50'
      )}
      tabIndex={0}
      role="article"
      aria-label={n.title || 'Notification'}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !n.is_read) onRead();
        if (e.key === 'Delete') onDelete();
      }}
    >
      {/* Unread stripe — Flowman blue */}
      <span
        aria-hidden
        className={cn(
          'absolute left-0 top-0 h-full w-1 rounded-l-lg',
          !n.is_read ? 'bg-[#4299E1]' : 'bg-transparent'
        )}
      />
      <div className="mt-0.5">
        <TypePill type={n.type} isRead={n.is_read} />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-start">
          <p className="flex-1 text-sm font-medium text-slate-900 truncate">
            {n.title || 'Notification'}
          </p>
          <div className="ml-2 hidden sm:flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
            {!n.is_read && (
              <button
                className="h-7 w-7 grid place-items-center rounded-md hover:bg-slate-100"
                onClick={onRead}
                title="Mark as read"
              >
                <Check className="h-4 w-4 text-emerald-600" />
              </button>
            )}
            <button
              className="h-7 w-7 grid place-items-center rounded-md hover:bg-slate-100"
              onClick={onDelete}
              title="Delete"
            >
              <Trash2 className="h-4 w-4 text-red-600" />
            </button>
            <button
              className="h-7 w-7 grid place-items-center rounded-md hover:bg-slate-100"
              aria-label="More"
              title="More"
            >
              <MoreVertical className="h-4 w-4 text-slate-600" />
            </button>
          </div>
        </div>

        {/* Scrollable message area inside each card */}
        <div className="mt-1 max-h-20 overflow-y-auto thin-scroll pr-1">
          <p className="text-sm leading-snug text-slate-600 whitespace-pre-wrap">{n.message}</p>
        </div>

        {!!timeLabel && <p className="mt-1 text-[11px] text-slate-400">{timeLabel}</p>}

        {/* Mobile actions */}
        <div className="mt-2 flex gap-2 sm:hidden">
          {!n.is_read && (
            <button
              className="px-2.5 py-1.5 text-xs rounded-md border border-emerald-500 text-emerald-600 hover:bg-emerald-50"
              onClick={onRead}
            >
              <div className="flex items-center gap-1">
                <Check className="h-3.5 w-3.5" /> Read
              </div>
            </button>
          )}
          <button
            className="px-2.5 py-1.5 text-xs rounded-md border border-red-500 text-red-600 hover:bg-red-50"
            onClick={onDelete}
          >
            <div className="flex items-center gap-1">
              <Trash2 className="h-3.5 w-3.5" /> Delete
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}

const TabsBar = ({ tab, setTab, counts }) => (
  <div className="px-4 pt-3">
    <div className="inline-flex rounded-md border bg-white">
      {[
        { key: 'all', label: `All (${counts.all})` },
        { key: 'unread', label: `Unread (${counts.unread})` },
      ].map((t) => (
        <button
          key={t.key}
          onClick={() => setTab(t.key)}
          className={cn(
            'px-3 py-1.5 text-sm',
            tab === t.key ? 'text-[#4299E1] bg-[#EBF8FF]' : 'text-[#4A5568]'
          )}
        >
          {t.label}
        </button>
      ))}
    </div>
  </div>
);

export default function NotificationCenter({
  notifications,
  setNotifications,
  setUnreadCount,
  closeCenter,
}) {
  const [tab, setTab] = useState('all');

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.is_read).length,
    [notifications]
  );

  const data = useMemo(() => {
    const list = tab === 'unread' ? notifications.filter((n) => !n.is_read) : notifications;
    return groupByDate(list);
  }, [tab, notifications]);

  // ---- API actions ----
  const markAsRead = async (id) => {
    try {
      const res = await TokenChecker('/notifications/mark_as_read/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: [id] }),
      });
      if (res.ok) {
        const updated = notifications.map((n) =>
          n.id === id ? { ...n, is_read: true } : n
        );
        setNotifications(updated);
        setUnreadCount(updated.filter((n) => !n.is_read).length);
      }
    } catch (err) {
      logError('Failed to mark as read', err);
    }
  };

  const deleteNotification = async (id) => {
    try {
      const res = await TokenChecker('/notifications/delete/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: [id] }),
      });
      if (res.ok) {
        const updated = notifications.filter((n) => n.id !== id);
        setNotifications(updated);
        setUnreadCount(updated.filter((n) => !n.is_read).length);
      }
    } catch (err) {
      logError('Failed to delete notification', err);
    }
  };

  const bulkMarkAsRead = async () => {
    const ids = notifications.filter((n) => !n.is_read).map((n) => n.id);
    if (ids.length === 0) return;
    try {
      const res = await TokenChecker('/notifications/mark_as_read/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids }),
      });
      if (res.ok) {
        const updated = notifications.map((n) => ({ ...n, is_read: true }));
        setNotifications(updated);
        setUnreadCount(0);
      }
    } catch (err) {
      logError('Bulk mark as read failed', err);
    }
  };

  const clearRead = async () => {
    const read = notifications.filter((n) => n.is_read);
    if (read.length === 0) return;
    const ids = read.map((n) => n.id);
    try {
      const res = await TokenChecker('/notifications/delete/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids }),
      });
      if (res.ok) {
        const updated = notifications.filter((n) => !n.is_read);
        setNotifications(updated);
        setUnreadCount(updated.filter((n) => !n.is_read).length);
      }
    } catch (err) {
      logError('Clear read failed', err);
    }
  };

  return (
    <div className="bg-white border shadow-lg rounded-xl overflow-hidden w-full max-w-lg flex flex-col">
      <ScrollbarStyles />

      {/* Header */}
      <div className="px-4 py-3 border-b flex items-center gap-3">
        <div className="flex items-center gap-2">
          <Bell className="h-5 w-5 text-slate-600" aria-hidden />
         
        </div>

        <div className="ml-auto flex items-center gap-2">
          <button
            className={cn(
              'px-2.5 py-1.5 text-xs rounded-md border',
              unreadCount === 0
                ? 'opacity-50 cursor-not-allowed border-slate-200 text-slate-400'
                : 'border-slate-300 text-slate-700 hover:bg-slate-50'
            )}
            onClick={bulkMarkAsRead}
            disabled={unreadCount === 0}
            title="Mark all as read"
          >
            <div className="flex items-center gap-1">
              <Check className="h-3.5 w-3.5" /> Mark all read
            </div>
          </button>

          <button
            className={cn(
              'px-2.5 py-1.5 text-xs rounded-md border',
              notifications.every((n) => !n.is_read)
                ? 'opacity-50 cursor-not-allowed border-slate-200 text-slate-400'
                : 'border-slate-300 text-slate-700 hover:bg-slate-50'
            )}
            onClick={clearRead}
            disabled={notifications.every((n) => !n.is_read)}
            title="Clear read"
          >
            <div className="flex items-center gap-1">
              <Trash2 className="h-3.5 w-3.5" /> Clear read
            </div>
          </button>

          <button
            className="px-2.5 py-1.5 text-xs rounded-md hover:bg-slate-50"
            onClick={closeCenter}
            title="Close"
          >
            Close
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className='mb-2 flex items-center justify-center'>
      <TabsBar tab={tab} setTab={setTab} counts={{ all: notifications.length, unread: unreadCount }} />
      </div>

      {/* Content (global scroll with visible bar) */}
      <div className="max-h-[400px] overflow-y-auto thin-scroll border-t">
        {notifications.length === 0 ? (
          <Empty />
        ) : (
          <div role="list" aria-label="Notifications" className="p-2 sm:p-3">
            {Object.entries(data).map(([section, items]) => (
              <section key={section} className="mb-3">
                <div className="px-2 py-1 text-xs font-medium text-slate-500 uppercase tracking-wide">
                  {section}
                </div>
                <ul className="mt-1 space-y-2">
                  {items.map((n) => (
                    <li key={n.id}>
                      <Item
                        n={n}
                        onRead={() => markAsRead(n.id)}
                        onDelete={() => deleteNotification(n.id)}
                      />
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
